import { challengeLanguage } from "../../../../packages/localization/src/core";
import { t as msg, currentLocale } from "../lib/i18n";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
export interface Config {
  local: boolean;
  turnstileMode: string;
  turnstileSiteKey: string | null;
  features: { googleLogin: boolean; appleLogin: boolean };
  legal: {
    entity: string | null;
    country: string | null;
    email: string | null;
    approved: boolean;
  };
}
let config: Promise<Config> | undefined;
export const getConfig = () =>
  (config ||= api<Config>("/api/config").catch((e) => {
    config = undefined;
    throw e;
  }));
interface Turnstile {
  render: (node: HTMLElement, options: Record<string, unknown>) => string;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}
let script: Promise<void> | undefined;
function load() {
  return (script ||= new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      script = undefined;
      s.remove();
      reject(
        new Error(msg("Security check could not load. Please reload this page.")),
      );
    };
    document.head.append(s);
  }));
}
export function Challenge({
  action,
  onToken,
  attempt = 0,
}: {
  action: string;
  onToken: (t: string) => void;
  attempt?: number;
}) {
  const node = useRef<HTMLDivElement>(null),
    callback = useRef(onToken),
    [error, setError] = useState("");
  callback.current = onToken;
  useEffect(() => {
    let live = true,
      widget: string | undefined;
    callback.current("");
    setError("");
    getConfig()
      .then(async (c) => {
        if (c.local && c.turnstileMode === "local") {
          if (live) callback.current("local");
          return;
        }
        if (!c.turnstileSiteKey)
          throw new Error(
            msg("Security verification is not configured. Please try later."),
          );
        await load();
        if (!live || !node.current) return;
        widget = window.turnstile!.render(node.current, {
          sitekey: c.turnstileSiteKey,
          action,
          language: challengeLanguage(currentLocale()),
          callback: (t: string) => { if (live) callback.current(t); },
          "expired-callback": () => { if (live) callback.current(""); },
          "error-callback": () => {
            if (!live) return;
            callback.current("");
            setError(msg("Please retry the security check."));
          },
        });
      })
      .catch((e) => {
        if (live) setError(e.message);
      });
    return () => {
      live = false;
      if (widget) window.turnstile?.remove(widget);
    };
  }, [action, attempt, currentLocale()]);
  return (
    <>
      <div ref={node} />
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
